import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { TrendingUp, CheckCircle2, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

import API from '../utils/api';

const ResetPasswordPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      setError(language === 'de' ? 'Ungültiger Reset-Link' : 'Invalid reset link');
    }
  }, [token, language]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (newPassword !== confirmPassword) {
      setError(language === 'de' ? 'Passwörter stimmen nicht überein' : 'Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      setError(language === 'de' ? 'Passwort muss mindestens 6 Zeichen lang sein' : 'Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: newPassword }),
      });
      
      if (response.ok) {
        setSuccess(true);
        setTimeout(() => navigate('/login'), 3000);
      } else {
        const data = await response.json();
        setError(data.detail || (language === 'de' ? 'Fehler beim Zurücksetzen' : 'Error resetting password'));
      }
    } catch (err) {
      setError(language === 'de' ? 'Netzwerkfehler' : 'Network error');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="glass w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <CheckCircle2 className="h-10 w-10 text-emerald-400" />
            </div>
            <h2 className="text-2xl font-bold mb-2">
              {language === 'de' ? 'Passwort geändert!' : 'Password Changed!'}
            </h2>
            <p className="text-muted-foreground mb-4">
              {language === 'de' 
                ? 'Sie werden zur Login-Seite weitergeleitet...' 
                : 'Redirecting to login page...'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <TrendingUp className="h-8 w-8 text-primary" />
          <span className="text-2xl font-heading font-bold">TradeJourn<span className="text-primary">.ai</span></span>
        </Link>
        
        <Card className="glass">
          <CardHeader>
            <CardTitle>{language === 'de' ? 'Neues Passwort' : 'New Password'}</CardTitle>
            <CardDescription>
              {language === 'de' 
                ? 'Geben Sie Ihr neues Passwort ein'
                : 'Enter your new password'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <div className="mb-4 p-3 rounded-lg bg-rose-500/10 border border-rose-500/30 flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-rose-400" />
                <p className="text-sm text-rose-400">{error}</p>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>{language === 'de' ? 'Neues Passwort' : 'New Password'}</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={language === 'de' ? 'Mindestens 6 Zeichen' : 'At least 6 characters'}
                  required
                />
              </div>
              
              <div>
                <Label>{language === 'de' ? 'Passwort bestätigen' : 'Confirm Password'}</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={language === 'de' ? 'Passwort wiederholen' : 'Repeat password'}
                  required
                />
              </div>
              
              <Button type="submit" className="w-full" disabled={loading}>
                {loading 
                  ? (language === 'de' ? 'Speichere...' : 'Saving...') 
                  : (language === 'de' ? 'Passwort ändern' : 'Change Password')}
              </Button>
            </form>
            
            <div className="mt-4 text-center">
              <Link to="/login" className="text-sm text-primary hover:underline">
                {language === 'de' ? 'Zurück zum Login' : 'Back to Login'}
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
